import React, { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';

// Discovery view: polls /api/magmon/inbox every 5s and shows the latest
// raw POSTs from MagMon (or any device using the magmon ingest token).
// Once we know the actual payload shape, this gets replaced by a real
// dashboard reading from a properly-typed table.

const MagmonInbox = ({ user }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInbox = async () => {
    try {
      const res = await fetch('/api/magmon/inbox');
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || `Status ${res.status}`);
      }
      const data = await res.json();
      setItems(Array.isArray(data.data) ? data.data : []);
      setLastRefreshed(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInbox();
    const interval = setInterval(fetchInbox, 5000);
    return () => clearInterval(interval);
  }, []);

  // mysql2 returns JSON columns as already-parsed objects; older drivers
  // hand back strings. Normalize defensively so we always render an object.
  const toObject = (val) => {
    if (val == null) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch (_) { return val; }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  };

  return (
    <div className="content-card" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Activity size={22} color="#2563eb" />
            MagMon Inbox
          </h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9rem' }}>
            Discovery view — latest 50 POSTs to <code>/api/magmon/ingest/&lt;token&gt;</code>. Auto-refreshes every 5 seconds.
          </p>
        </div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', textAlign: 'right' }}>
          {loading && !lastRefreshed && <div>Loading…</div>}
          {lastRefreshed && <div>Last refresh: {lastRefreshed.toLocaleTimeString()}</div>}
          {error && <div style={{ color: '#dc2626', marginTop: '0.25rem' }}>Error: {error}</div>}
        </div>
      </div>

      {items.length === 0 && !loading ? (
        <div style={{
          padding: '2.5rem',
          textAlign: 'center',
          color: '#9ca3af',
          background: '#f9fafb',
          border: '1px dashed #d1d5db',
          borderRadius: '0.5rem'
        }}>
          No data captured yet. Once MagMon is configured to POST to
          <code style={{ background: '#fff', padding: '0.1rem 0.35rem', margin: '0 0.25rem', borderRadius: '0.25rem' }}>
            /api/magmon/ingest/&lt;your-token&gt;
          </code>
          incoming payloads will appear here within a few seconds.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {items.map((item) => {
            const headers = toObject(item.headers);
            const body = toObject(item.body);
            return (
              <div key={item.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '1rem',
                background: '#fff'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <strong style={{ fontFamily: 'monospace', color: '#374151' }}>#{item.id}</strong>
                  <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    {formatTimestamp(item.received_at)} · <code>{item.source_ip}</code>
                  </span>
                </div>

                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}>
                    Body
                  </div>
                  <pre style={{
                    fontSize: '0.8rem',
                    background: '#f9fafb',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '0.25rem',
                    overflow: 'auto',
                    margin: 0,
                    maxHeight: '320px'
                  }}>
                    {body == null ? '(empty or non-JSON)' : JSON.stringify(body, null, 2)}
                  </pre>
                </div>

                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '0.85rem' }}>
                    Headers
                  </summary>
                  <pre style={{
                    fontSize: '0.75rem',
                    background: '#f9fafb',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '0.25rem',
                    overflow: 'auto',
                    margin: '0.5rem 0 0 0',
                    maxHeight: '240px'
                  }}>
                    {JSON.stringify(headers, null, 2)}
                  </pre>
                </details>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MagmonInbox;
